// ===========================================
// Event Venue Selector (4-mode)
// ===========================================
// Unified venue picker for the event editor. Exactly ONE of four shapes is
// persisted per event, and switching mode clears the others:
//
//   1. SCHOOL    → a school Location + Room (via LocationRoomPicker).
//                  Persists roomId + the derived locationId. venueId/venueName null.
//   2. SAVED     → a reusable EventVenue (Select). Persists venueId.
//                  locationId/roomId/venueName null.
//   3. NEW       → inline NewVenueForm → POST → becomes a SAVED selection.
//   4. CUSTOM    → free-text venue name only. Persists venueName.
//
// The selector is controlled: it derives its mode from the incoming
// `value` and emits a fully-normalised VenueSelection on every change. It also
// suggests a capacity default (Room.capacity / EventVenue.capacity) which the
// parent may apply — the parent remains the source of truth for capacity.

import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import LocationRoomPicker from '../admin/lessonDetail/LocationRoomPicker';
import NewVenueForm from './NewVenueForm';
import { useEventVenues } from '../../hooks/useEventVenues';
import { useLocations } from '../../hooks/useAdmin';
import { formatAddress } from '../../constants/countries';
import type { EventVenue } from '../../api/events.api';

export type VenueMode = 'SCHOOL' | 'SAVED' | 'NEW' | 'CUSTOM';

// The normalised venue shape persisted on the event. Exactly one of
// roomId / venueId / venueName is non-null (locationId rides with roomId).
export interface VenueSelection {
  locationId: string | null;
  roomId: string | null;
  venueId: string | null;
  venueName: string | null;
}

export const EMPTY_VENUE_SELECTION: VenueSelection = {
  locationId: null,
  roomId: null,
  venueId: null,
  venueName: null,
};

interface EventVenueSelectorProps {
  value: VenueSelection;
  onChange: (selection: VenueSelection) => void;
  /** Called with a suggested capacity when the chosen room/venue has one. */
  onSuggestCapacity?: (capacity: number) => void;
}

/** Derive the active mode from a persisted selection. */
function deriveMode(value: VenueSelection): VenueMode {
  if (value.roomId) return 'SCHOOL';
  if (value.venueId) return 'SAVED';
  if (value.venueName) return 'CUSTOM';
  return 'SCHOOL';
}

export default function EventVenueSelector({
  value,
  onChange,
  onSuggestCapacity,
}: EventVenueSelectorProps) {
  const [mode, setMode] = useState<VenueMode>(() => deriveMode(value));
  const { data: venues = [], isLoading: isLoadingVenues } = useEventVenues();
  const { data: locations = [] } = useLocations();

  // Keep the mode in sync when an existing event loads asynchronously (the
  // initial state ran before `value` was populated from the fetched event).
  useEffect(() => {
    setMode(deriveMode(value));
    // Only react to identity-defining fields, not capacity etc.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.roomId, value.venueId, value.venueName]);

  // Map a chosen roomId back to its locationId by walking the locations tree.
  const findLocationIdForRoom = (roomId: string): string | null => {
    for (const loc of locations) {
      const rooms = loc.Room ?? loc.rooms ?? [];
      if (rooms.some((r) => r.id === roomId)) return loc.id;
    }
    return null;
  };

  const roomCapacity = (roomId: string): number | undefined => {
    for (const loc of locations) {
      const rooms = loc.Room ?? loc.rooms ?? [];
      const room = rooms.find((r) => r.id === roomId);
      if (room) return room.capacity;
    }
    return undefined;
  };

  const handleModeChange = (next: VenueMode | null) => {
    if (!next || next === mode) return;
    setMode(next);
    // Switching mode clears all other shapes (one-shape invariant).
    onChange(EMPTY_VENUE_SELECTION);
  };

  // --- Mode 1: SCHOOL (location + room) ---
  const handleRoomChange = (roomId: string) => {
    if (!roomId) {
      onChange(EMPTY_VENUE_SELECTION);
      return;
    }
    const locationId = findLocationIdForRoom(roomId);
    onChange({ locationId, roomId, venueId: null, venueName: null });
    const cap = roomCapacity(roomId);
    if (cap != null && onSuggestCapacity) onSuggestCapacity(cap);
  };

  // --- Mode 2: SAVED (reusable venue) ---
  const handleVenueSelect = (venueId: string) => {
    if (!venueId) {
      onChange(EMPTY_VENUE_SELECTION);
      return;
    }
    onChange({ locationId: null, roomId: null, venueId, venueName: null });
    const venue = venues.find((v) => v.id === venueId);
    if (venue?.capacity != null && onSuggestCapacity) onSuggestCapacity(venue.capacity);
  };

  // --- Mode 3: NEW (create) → becomes SAVED ---
  const handleVenueCreated = (venue: EventVenue) => {
    setMode('SAVED');
    onChange({ locationId: null, roomId: null, venueId: venue.id, venueName: null });
    if (venue.capacity != null && onSuggestCapacity) onSuggestCapacity(venue.capacity);
  };

  // --- Mode 4: CUSTOM (free text) ---
  const handleCustomNameChange = (name: string) => {
    onChange({ locationId: null, roomId: null, venueId: null, venueName: name || null });
  };

  const selectedVenue = useMemo(
    () => venues.find((v) => v.id === value.venueId),
    [venues, value.venueId]
  );

  return (
    <Stack spacing={2}>
      <ToggleButtonGroup
        value={mode}
        exclusive
        onChange={(_, next) => handleModeChange(next as VenueMode | null)}
        size="small"
        color="primary"
      >
        <ToggleButton value="SCHOOL">School Location</ToggleButton>
        <ToggleButton value="SAVED">Saved Venue</ToggleButton>
        <ToggleButton value="NEW">New Venue</ToggleButton>
        <ToggleButton value="CUSTOM">Custom Name</ToggleButton>
      </ToggleButtonGroup>

      {/* Mode 1: school location + room */}
      {mode === 'SCHOOL' && (
        <LocationRoomPicker value={value.roomId ?? ''} onChange={handleRoomChange} />
      )}

      {/* Mode 2: saved venue */}
      {mode === 'SAVED' && (
        <Stack spacing={2}>
          <FormControl fullWidth disabled={isLoadingVenues}>
            <InputLabel id="event-venue-select-label">Venue</InputLabel>
            <Select
              labelId="event-venue-select-label"
              label="Venue"
              // While venues are loading the MenuItems don't exist yet; binding a
              // venueId here would trigger MUI's out-of-range-value warning, so
              // hold the value empty until the options are present.
              value={isLoadingVenues ? '' : value.venueId ?? ''}
              onChange={(e) => handleVenueSelect(e.target.value)}
              endAdornment={isLoadingVenues ? <CircularProgress size={18} sx={{ mr: 3 }} /> : null}
            >
              {venues.length === 0 && (
                <MenuItem value="" disabled>
                  No saved venues yet
                </MenuItem>
              )}
              {venues.map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {v.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedVenue && (
            <Typography variant="caption" color="text.secondary">
              {formatAddress(selectedVenue) || 'No address on file'}
              {selectedVenue.capacity != null && ` · capacity ${selectedVenue.capacity}`}
            </Typography>
          )}
        </Stack>
      )}

      {/* Mode 3: create a new venue → becomes a SAVED selection */}
      {mode === 'NEW' && (
        <NewVenueForm
          onCreated={handleVenueCreated}
          onCancel={() => handleModeChange('SAVED')}
        />
      )}

      {/* Mode 4: free-text name */}
      {mode === 'CUSTOM' && (
        <Box>
          <TextField
            label="Venue Name"
            value={value.venueName ?? ''}
            onChange={(e) => handleCustomNameChange(e.target.value)}
            fullWidth
            placeholder="e.g. School Hall, Main Studio"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            A plain text venue name with no saved address or map.
          </Typography>
        </Box>
      )}

      {mode === 'SCHOOL' && locations.length === 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          No school locations configured. Use a saved venue or a custom name instead.
        </Alert>
      )}
    </Stack>
  );
}
